import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { colorFromBgColor } from '@/utils';

interface ITooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  content: string;
  placement?: 'left' | 'right' | 'bottom' | 'top';
  backgroundColor?: string;
  inactive?: boolean;
  children: React.ReactNode;
}

const Tooltip = ({ placement, content, backgroundColor, inactive, children, className, ...props }: ITooltipProps) => {
  const [visible, setVisible] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const timeout = useRef<NodeJS.Timeout>(null);

  let placementClass = 'top-[calc(100%+10px)] -translate-y-1 group-hover/tooltip:translate-0';
  let containerClass = 'top-0';
  let containerVisivilityClass = 'h-auto w-full';
  if (placement === 'top') {
    placementClass = 'top-[calc(-100%)] translate-y-1 group-hover/tooltip:translate-y-0';
  } else if (placement === 'right') {
    placementClass = 'left-[calc(100%+10px)] top-0 -translate-x-1 group-hover/tooltip:translate-x-0 h-full w-0';
    containerClass = 'left-0 top-1/2 -translate-y-1/2';
    containerVisivilityClass = 'w-auto';
  } else if (placement === 'left') {
    placementClass = 'right-[calc(100%+10px)] top-0 translate-x-1 group-hover/tooltip:translate-x-0 h-full w-0';
    containerClass = 'right-0 top-1/2 -translate-y-1/2';
    containerVisivilityClass = 'w-auto';
  }

  useEffect(() => {
    if (container.current && (!placement || placement === 'top' || placement === 'bottom')) {
      const boundingClientRect = container.current?.getBoundingClientRect();
      if (boundingClientRect && boundingClientRect.x < 10) {
        container.current.style.left = '0';
      } else if (boundingClientRect && boundingClientRect.x + boundingClientRect.width > window.innerWidth - 10) {
        container.current.style.right = '0';
      }
    }
  }, [placement]);

  const onMouseEnter = () => {
    if (!inactive) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      setVisible(true);
    }
  };
  const onMouseLeave = () => {
    timeout.current = setTimeout(() => {
      setVisible(false);
      timeout.current = null;
    }, 300);
  };

  return (
    <div
      className={classNames('tooltip group/tooltip relative', className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      {children}

      <div
        className={classNames(
          'tooltip-container absolute flex items-center justify-center opacity-0 transition-all duration-300 group-hover/tooltip:opacity-100',
          placementClass,
          visible ? containerVisivilityClass : 'overflow-hidden',
        )}
      >
        <div
          ref={container}
          className={classNames(
            'pointer-events-none absolute w-max',
            containerClass,
            visible ? 'h-auto' : 'h-0 overflow-hidden',
          )}
        >
          <div
            className="rounded-xl px-3 py-1.5 text-sm"
            style={{
              backgroundColor: backgroundColor || '#000000a8',
              color: colorFromBgColor(backgroundColor || '#000') || '#fff',
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </div>
    </div>
  );
};

export default Tooltip;
